import React, { useEffect, useMemo, useState } from 'react';
import { S, Field, Select, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';

// Stores module — Reports. One screen for every Stores report, grouped like the
// Stores menu. The backend (storesReports.js) sends the columns with each
// report, so this screen only renders: filters, table with totals, Excel, print.

const n = (v) => Number(v) || 0;
const fmt = (col, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (col.type === 'money') return fmtMoney(v);
  if (col.type === 'qty') return fmtQty(v);
  if (col.type === 'num') return Number(v).toLocaleString('en-IN');
  if (col.type === 'date') return fmtDate(v);
  return String(v);
};
const isNum = (col) => col.type === 'money' || col.type === 'qty' || col.type === 'num';
const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

const linkBtn = { border: 'none', background: 'none', color: '#1e40af', cursor: 'pointer', padding: 0, fontSize: '0.75rem', fontWeight: 600 };

// Which menu groups are expanded is remembered per browser.
const OPEN_KEY = 'stores.reports.openGroups';
const loadOpen = () => {
  try { const v = JSON.parse(localStorage.getItem(OPEN_KEY)); return Array.isArray(v) ? v : null; } catch (e) { return null; }
};
const saveOpen = (list) => {
  try { localStorage.setItem(OPEN_KEY, JSON.stringify(list)); } catch (e) { /* storage blocked */ }
};

export default function StoresReports({ api, io, boot, property }) {
  const [catalog, setCatalog] = useState(null);
  const [key, setKey] = useState('');
  const [f, setF] = useState({ from: `${today().slice(0, 8)}01`, to: today(), store_id: '', department_id: '', vendor_id: '' });
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(() => new Set(loadOpen() || []));
  const [find, setFind] = useState('');

  const changeOpen = (next) => { setOpen(next); saveOpen([...next]); };
  const toggleGroup = (g) => {
    const next = new Set(open);
    if (next.has(g)) next.delete(g); else next.add(g);
    changeOpen(next);
  };

  useEffect(() => {
    api('/reports/catalog').then((c) => {
      setCatalog(c);
      setKey((k) => k || c[0]?.reports[0]?.key || '');
      // First visit: open only the group of the selected report.
      if (!loadOpen() && c[0]) { const first = new Set([c[0].group]); setOpen(first); saveOpen([...first]); }
    }).catch((e) => setErr(e.message));
    api('/vendors').then((v) => setVendors(Array.isArray(v) ? v : [])).catch(() => setVendors([]));
  }, [api]);

  const rep = useMemo(() => (catalog || []).flatMap((g) => g.reports).find((r) => r.key === key), [catalog, key]);
  const uses = (name) => Boolean(rep?.filters.includes(name));
  const qs = useMemo(() => {
    if (!rep) return '';
    const has = (name) => rep.filters.includes(name);
    const p = {};
    if (has('from')) p.from = f.from;
    if (has('to') || has('from')) p.to = f.to;
    if (has('store') && f.store_id) p.store_id = f.store_id;
    if (has('department') && f.department_id) p.department_id = f.department_id;
    if (has('vendor') && f.vendor_id) p.vendor_id = f.vendor_id;
    return new URLSearchParams(p).toString();
  }, [rep, f]);

  useEffect(() => {
    if (!rep) return undefined;
    let live = true;
    setLoading(true);
    setErr('');
    api(`/reports/run/${rep.key}?${qs}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) { setErr(e.message); setData(null); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [api, rep, qs]);

  const rows = useMemo(() => {
    if (!data || data.key !== key) return [];
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return data.rows;
    return data.rows.filter((r) => {
      const hay = data.columns.map((c) => String(r[c.key] ?? '')).join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [data, key, q]);
  // Menu groups, narrowed by the "Find a report" box. While searching, every
  // group with a match is shown expanded.
  const findWords = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searching = findWords.length > 0;
  const menu = (catalog || [])
    .map((g) => ({ group: g.group, all: g.reports, reports: searching ? g.reports.filter((r) => findWords.every((w) => r.title.toLowerCase().includes(w))) : g.reports }))
    .filter((g) => g.reports.length);

  const cols = data && data.key === key ? data.columns : [];
  const totals = cols.some((c) => c.sum) ? cols.map((c) => (c.sum ? rows.reduce((t, r) => t + n(r[c.key]), 0) : null)) : null;
  const period = uses('from') ? `${fmtDate(f.from)} to ${fmtDate(f.to)}` : uses('to') ? `Up to ${fmtDate(f.to)}` : `As on ${fmtDate(today())}`;

  const download = () => {
    setErr('');
    io.download(`/reports/run/${key}?${qs}${qs ? '&' : ''}format=xlsx`, `${rep.title.replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`).catch((e) => setErr(e.message));
  };

  const print = () => {
    const w = window.open('', '_blank');
    if (!w) { setErr('Allow pop-ups for this site to print the report.'); return; }
    const th = cols.map((c) => `<th class="${isNum(c) ? 'r' : ''}">${esc(c.label)}</th>`).join('');
    const body = rows.map((r) => `<tr>${cols.map((c) => `<td class="${isNum(c) ? 'r' : ''}">${esc(fmt(c, r[c.key]))}</td>`).join('')}</tr>`).join('');
    const foot = totals ? `<tr class="t">${cols.map((c, i) => `<td class="${isNum(c) ? 'r' : ''}">${i === 0 ? 'Total' : totals[i] === null ? '' : esc(fmt(c, totals[i]))}</td>`).join('')}</tr>` : '';
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(rep.title)}</title><style>
      body{font-family:Arial,sans-serif;font-size:11px;color:#0f172a;margin:16px}h2{margin:0;font-size:16px}p{margin:2px 0 10px;color:#475569}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #cbd5e1;padding:4px 6px;text-align:left}th{background:#f1f5f9}
      .r{text-align:right;white-space:nowrap}.t td{font-weight:700;background:#f8fafc}@page{size:landscape;margin:10mm}</style></head><body>
      <h2>${esc(property?.name || '')} — ${esc(rep.title)}</h2><p>${esc(period)} · ${rows.length} rows · printed ${esc(new Date().toLocaleString('en-IN'))}</p>
      <table><thead><tr>${th}</tr></thead><tbody>${body}${foot}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  if (!catalog) return err ? <div style={S.err}>{err}</div> : <div style={S.empty}>Loading reports…</div>;
  if (!catalog.length) return <div style={S.empty}>You have no Stores reports. Ask your Hotel Admin for Stores access.</div>;

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ ...S.card, flex: '0 0 260px', maxWidth: '100%', padding: '0.5rem', marginBottom: 0 }}>
        <div style={{ padding: '0.15rem 0.15rem 0.5rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.35rem' }}>
          <input style={{ ...S.inp, width: '100%' }} value={find} placeholder="Find a report…" onChange={(e) => setFind(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <button type="button" style={linkBtn} onClick={() => { setFind(''); changeOpen(new Set(catalog.map((g) => g.group))); }}>Expand all</button>
            <button type="button" style={linkBtn} onClick={() => { setFind(''); changeOpen(new Set()); }}>Collapse all</button>
          </div>
        </div>
        {menu.map((g) => {
          const isOpen = searching || open.has(g.group);
          const hasActive = g.all.some((r) => r.key === key);
          const flag = hasActive && !isOpen;
          return (
            <div key={g.group} style={{ marginBottom: '0.15rem' }}>
              <button type="button" aria-expanded={isOpen} onClick={() => { if (!searching) toggleGroup(g.group); }}
                style={{ ...S.lbl, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6, width: '100%', border: 'none', background: 'none', borderRadius: 6,
                  padding: '0.45rem 0.4rem', cursor: searching ? 'default' : 'pointer', textAlign: 'left', color: flag ? '#1e40af' : '#475569' }}>
                <span style={{ display: 'inline-block', width: 10, fontSize: '0.6rem', transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                <span style={{ flex: 1 }}>{g.group}</span>
                <span style={{ background: flag ? '#dbeafe' : '#e2e8f0', color: flag ? '#1e40af' : '#475569', borderRadius: 999, padding: '0 7px', fontSize: '0.68rem', letterSpacing: 0 }}>{g.reports.length}</span>
              </button>
              {isOpen && (
                <div style={{ padding: '0 0 0.35rem 0.85rem' }}>
                  {g.reports.map((r) => (
                    <button key={r.key} type="button" onClick={() => { setKey(r.key); setQ(''); }} title={r.hint}
                      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 6, padding: '0.35rem 0.5rem', cursor: 'pointer', fontSize: '0.84rem',
                        background: r.key === key ? '#dbeafe' : 'transparent', color: r.key === key ? '#1e40af' : '#334155', fontWeight: r.key === key ? 700 : 500 }}>
                      {r.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {searching && !menu.length && <div style={{ ...S.empty, padding: '0.75rem' }}>No report matches.</div>}
      </div>

      <div style={{ flex: '1 1 600px', minWidth: 0 }}>
        {rep && (
          <div style={S.card}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a5f' }}>{rep.title}</h3>
            <p style={{ ...S.sub, marginBottom: '0.75rem' }}>{rep.hint}</p>
            <div style={{ ...S.bar, marginBottom: 0 }}>
              {uses('from') && <Field label="From"><input type="date" style={S.inp} value={f.from} max={f.to} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>}
              {(uses('to') || uses('from')) && <Field label={uses('from') ? 'To' : 'Up to'}><input type="date" style={S.inp} value={f.to} min={uses('from') ? f.from : undefined} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>}
              {uses('department') && (
                <Field label="Department">
                  <Select style={{ ...S.inp, minWidth: 160 }} value={f.department_id} onChange={(e) => setF({ ...f, department_id: e.target.value, store_id: '' })}>
                    <option value="">All departments</option>
                    {boot.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </Field>
              )}
              {uses('store') && (
                <Field label="Store">
                  <Select style={{ ...S.inp, minWidth: 170 }} value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })}>
                    <option value="">All stores</option>
                    {boot.stores.filter((s) => !f.department_id || s.department_id === f.department_id).map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
                  </Select>
                </Field>
              )}
              {uses('vendor') && (
                <Field label="Vendor">
                  <Select style={{ ...S.inp, minWidth: 190 }} value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value })}>
                    <option value="">All vendors</option>
                    {vendors.map((v) => <option key={v.id} value={String(v.id)}>{v.vendor_name}</option>)}
                  </Select>
                </Field>
              )}
              <Field label="Search rows"><input style={{ ...S.inp, minWidth: 180 }} value={q} placeholder="Item, doc no., vendor…" onChange={(e) => setQ(e.target.value)} /></Field>
              <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto' }}>
                <button type="button" className="btn-muted" onClick={download} disabled={loading || !cols.length}>⬇ Excel</button>
                <button type="button" className="btn-muted" onClick={print} disabled={loading || !cols.length}>🖨 Print</button>
              </div>
            </div>
          </div>
        )}
        {err && <div style={S.err}>{err}</div>}
        <div style={{ ...S.sub, margin: '0 0 0.4rem' }}>{loading ? 'Loading…' : `${period} · ${rows.length} row${rows.length === 1 ? '' : 's'}${q ? ` matching "${q}"` : ''}`}</div>
        <div style={{ ...S.tableWrap, maxHeight: '65vh', overflow: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{cols.map((c) => <th key={c.key} style={{ ...S.th, ...(isNum(c) ? S.num : {}), position: 'sticky', top: 0, zIndex: 1 }}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>{cols.map((c) => <td key={c.key} style={{ ...S.td, ...(isNum(c) ? S.num : {}) }}>{fmt(c, r[c.key])}</td>)}</tr>
              ))}
              {!loading && cols.length > 0 && !rows.length && <tr><td colSpan={cols.length} style={S.empty}>Nothing to show for these filters.</td></tr>}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr>
                  {cols.map((c, i) => (
                    <td key={c.key} style={{ ...S.td, ...(isNum(c) ? S.num : {}), fontWeight: 800, background: '#f8fafc', position: 'sticky', bottom: 0 }}>
                      {i === 0 ? 'Total' : totals[i] === null ? '' : fmt(c, totals[i])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
